// Plays the prerendered, cached demo clips (real Soniox audio shipped as static
// assets under public/demo/) overlapping, with a live per-voice gain.

export interface DemoVoice {
  mode: string;
  label: string;
  line: string;
  level: number; // 0..100 starting loudness
  file: string;
  position?: [number, number, number]; // HRTF-panned spot in the room (headphones)
}

interface Loaded {
  gain: GainNode;
  buffer: AudioBuffer;
  source?: AudioBufferSourceNode;
}

export interface PlayOpts {
  rate?: number;    // playbackRate (defusion: slow/fast)
  muffled?: boolean; // lowpass + quiet (defusion: far away)
}

export interface DemoPlayer {
  play(opts?: PlayOpts): Promise<void>;
  playSequence(): Promise<void>;
  setGain(index: number, value: number): void;
  close(): void;
}

export async function loadDemoPlayer(voices: DemoVoice[]): Promise<DemoPlayer> {
  const Ctor: typeof AudioContext =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctor();

  const loaded: Loaded[] = await Promise.all(
    voices.map(async (v) => {
      const resp = await fetch(v.file);
      if (!resp.ok) throw new Error(`missing demo asset: ${v.file}`);
      const buffer = await ctx.decodeAudioData(await resp.arrayBuffer());
      const gain = ctx.createGain();
      gain.gain.value = v.level / 100;
      if (v.position) {
        const panner = ctx.createPanner();
        panner.panningModel = "HRTF";
        const [x, y, z] = v.position;
        if (panner.positionX) {
          panner.positionX.value = x;
          panner.positionY.value = y;
          panner.positionZ.value = z;
        } else {
          panner.setPosition(x, y, z); // Safari fallback
        }
        gain.connect(panner);
        panner.connect(ctx.destination);
      } else {
        gain.connect(ctx.destination);
      }
      return { buffer, gain };
    })
  );

  // Bumped on every stop/replay; a stale token cancels a pending sequence chain.
  let token = 0;

  const stop = () => {
    token++;
    loaded.forEach((l) => {
      try {
        l.source?.stop();
      } catch {
        /* already stopped */
      }
      l.source = undefined;
    });
  };

  return {
    async play(opts: PlayOpts = {}) {
      if (ctx.state === "suspended") await ctx.resume();
      stop();
      loaded.forEach((l) => {
        const source = ctx.createBufferSource();
        source.buffer = l.buffer;
        if (opts.rate) source.playbackRate.value = opts.rate;
        if (opts.muffled) {
          const filter = ctx.createBiquadFilter();
          filter.type = "lowpass";
          filter.frequency.value = 700;
          const quiet = ctx.createGain();
          quiet.gain.value = 0.4;
          source.connect(filter);
          filter.connect(quiet);
          quiet.connect(l.gain);
        } else {
          source.connect(l.gain);
        }
        source.start();
        l.source = source;
      });
    },
    async playSequence() {
      if (ctx.state === "suspended") await ctx.resume();
      stop();
      const tok = ++token;
      const startAt = (i: number) => {
        if (tok !== token || i >= loaded.length) return;
        const l = loaded[i];
        const source = ctx.createBufferSource();
        source.buffer = l.buffer;
        source.connect(l.gain);
        source.onended = () => startAt(i + 1);
        source.start();
        l.source = source;
      };
      startAt(0);
    },
    setGain(index, value) {
      const l = loaded[index];
      if (l) l.gain.gain.value = value;
    },
    close() {
      stop();
      ctx.close();
    },
  };
}
